const HORSE_CURRENT_STATUS_LABELS = {
  active: 'Active',
  for_sale: 'For Sale',
  sale_pending: 'Sale Pending',
  sold: 'Sold',
  deceased: 'Deceased',
  retired: 'Retired',
  lease_terminated: 'Lease terminated',
};

export function formatHorseCurrentStatus(status) {
  if (!status) return '';
  return HORSE_CURRENT_STATUS_LABELS[status] ?? status.replace(/_/g, ' ');
}

/**
 * Status filter options for HorsesPage, including the synthetic "current"
 * value (see isCurrentHorse) alongside every real current_status value.
 */
export const ALL_HORSE_STATUSES = [
  { value: 'current', label: 'Current' },
  ...Object.entries(HORSE_CURRENT_STATUS_LABELS).map(([value, label]) => ({ value, label })),
];

const CURRENT_HORSE_STATUSES = ['active', 'for_sale', 'sale_pending', 'retired'];

/**
 * A horse eligible for scheduling: either an owned horse in one of the
 * sale-lifecycle statuses the dashboard/horses-page "Current" filter uses,
 * or a leased horse whose lease hasn't been terminated. current_status's
 * sale-lifecycle values (active/for_sale/sale_pending/retired) don't apply
 * to leased horses, so this is intentionally broader than that filter, not
 * an exact match of it. Reference-type horses (pedigree-only ancestor
 * records, never physically at the ranch) are never schedulable regardless
 * of status, matching the owner_type !== 'reference' convention used in
 * BreedingPage.js and FeedPage.js.
 */
export function isCurrentHorse(horse) {
  if (horse.owner_type === 'reference') return false;
  if (CURRENT_HORSE_STATUSES.includes(horse.current_status)) return true;
  return horse.owner_type === 'leased' && horse.current_status !== 'lease_terminated';
}

function dateInputFilled(value) {
  return value != null && String(value).trim() !== '';
}

/**
 * Keeps horse modal status in sync with sale date fields (matches API rules).
 * @param {Record<string, unknown>} prev - previous form state
 * @param {'date_sold' | 'date_sale_paid'} name
 * @param {string} value - new value for that field
 * @returns {string|undefined} new current_status, or undefined to leave it unchanged
 */
export function deriveCurrentStatusAfterSaleDateChange(prev, name, value) {
  if (name === 'date_sale_paid') {
    if (dateInputFilled(value)) return 'sold';
    return dateInputFilled(prev.date_sold) ? 'sale_pending' : 'active';
  }
  if (name === 'date_sold') {
    if (dateInputFilled(prev.date_sale_paid)) return undefined;
    const prevSold = dateInputFilled(prev.date_sold);
    const newSold = dateInputFilled(value);
    if (newSold && !prevSold) return 'sale_pending';
    if (!newSold && prevSold) return 'active';
    return undefined;
  }
  return undefined;
}
